import React, {FC, useEffect, useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {Row, Col, Card, Typography, Spin, Button, message, Space} from 'antd';
import advertiserService from '../../services/advertiserService';
import {formatString, formatValue, useIsMobile} from '../../utils/helpers';

const {Text} = Typography;

interface Advertiser {
    _id: string;
    username: string;
    userType: 'individual' | 'business';
    displayName: string;
    activeCampaigns: number;
    totalDeposited: number;
    totalSpent: number;
    totalClicks: number;
    averageClickRate: number;
}

const AdvertiserPage: FC = () => {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [advertiser, setAdvertiser] = useState<Advertiser | null>(null);
    const [loading, setLoading] = useState(true);

    const isMobile = useIsMobile();

    useEffect(() => {
        if (!id) return;

        (async () => {
            setLoading(true);

            try {
                const data: any = await advertiserService.getAdvertiser(id);
                setAdvertiser(data);
            } catch (err) {
                message.error('Failed to load advertiser details.');
            } finally {
                setLoading(false);
            }
        })();

    }, [id]);

    const handleViewCampaigns = async () => {
        if (!id) return;

        try {
            navigate(`/advertisers/${id}/campaigns`);
        } catch {
            message.error('Unable to load campaigns.');
        }
    };

    if (loading) return <Spin tip="Loading…"/>;

    if (!advertiser) return <Text>Advertiser not found.</Text>;

    return (
        <div style={{maxWidth: '90%', margin: 'auto', padding: isMobile ? 12 : 20}}>
            <label className={"title"}>Advertiser details</label>

            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Card>
                        <Text strong>Username:</Text> {advertiser.username}<br/>
                        <Text strong>User type:</Text> {formatString(advertiser.userType)}<br/>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card title="Name">{advertiser.displayName}</Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card title="Active campaigns">{advertiser.activeCampaigns}</Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card title="Total clicks">{advertiser.totalClicks}</Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card title="Total deposited">${formatValue(advertiser.totalDeposited)}</Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card title="Total spent">${formatValue(advertiser.totalSpent)}</Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card title="Average click rate">${formatValue(advertiser.averageClickRate)}</Card>
                </Col>
            </Row>
            <Row justify="end" style={{marginTop: 20}}>
                <div
                    style={{
                        display: 'flex',
                        gap: 8,
                        flexWrap: 'wrap',
                        justifyContent: isMobile ? 'stretch' : 'flex-end',
                        width: isMobile ? '100%' : 'auto',
                    }}
                >
                    <Button
                        type="primary"
                        onClick={handleViewCampaigns}
                        block={isMobile}
                        style={{width: isMobile ? '100%' : 'auto'}}
                    >
                        Campaigns
                    </Button>

                    <Button
                        type="default"
                        onClick={() => navigate(-1)}
                        block={isMobile}
                        style={{width: isMobile ? '100%' : 'auto'}}
                    >
                        Back
                    </Button>
                </div>
            </Row>
        </div>
    );
};

export default AdvertiserPage;
